"use client";

import { lazy, Suspense, useEffect, useState } from "react";
import { usePathname } from "next/navigation";

import OwnedPageViewTracker from "@/components/OwnedPageViewTracker";
import { captureFirstTouchLanding } from "@/lib/landing-cluster";
import { ensureGeoCountry } from "@/lib/geo";
import { shouldShowFloatingFeedback } from "@/lib/global-overlays";
import { isPerformanceCriticalPath } from "@/lib/performance-critical-paths";
import {
  isBrowserThirdPartyTelemetrySuppressed,
  isThirdPartyEmbedDocument,
  thirdPartyTelemetryAllowedForDocument,
} from "@/lib/telemetry-route-policy";

// React.lazy starts each import only when its activation branch actually
// renders. next/dynamic registered every inactive enhancement in the route
// manifest, so Lighthouse fetched their chunks before FCP even though the
// components were held behind 3.5s/30s policy timers.
const SupportHelper = lazy(() => import("@/components/SupportHelper"));
const FloatingFeedback = lazy(() => import("@/components/FloatingFeedback"));
const GoogleTagManager = lazy(() => import("@/components/GoogleTagManager"));
const DirectGoogleAnalytics = lazy(() => import("@/components/DirectGoogleAnalytics"));
const GlobalEngagementTracker = lazy(() => import("@/components/GlobalEngagementTracker"));
const CloudflareWebAnalytics = lazy(() => import("@/components/CloudflareWebAnalytics"));
const ShopifyAttribution = lazy(() => import("@/components/ShopifyAttribution"));
const GlobalAgentEntry = lazy(() => import("@/components/GlobalAgentEntry"));
const SEOPageRevenueTracker = lazy(() => import("@/components/SEOPageRevenueTracker"));
const MetaPixel = lazy(() => import("@/components/MetaPixel"));
const ExitIntent = lazy(() => import("@/components/ExitIntent"));
const NpsSurvey = lazy(() => import("@/components/NpsSurvey"));
const AstrologerPageTracker = lazy(() => import("@/components/AstrologerPageTracker"));

const VISIBLE_ENHANCEMENTS_DELAY_MS = 3500;
// First-party owned analytics loads on the first user interaction OR this
// idle/timeout fallback — whichever fires first. Industry standard is "~3.5s OR
// on interaction"; the old 8000ms idle-only silently dropped every session that
// bounced within ~8s. Keep <=4000ms (enforced by scripts/check-media-performance.mjs).
const ANALYTICS_ENHANCEMENTS_DELAY_MS = 3500;
// The native Google tag costs ~235ms of mobile main-thread boot work on the
// flagship page (PageSpeed, 2026-08-01). Owned page-view telemetry is already
// immediate, so the vendor tag can wait for a genuine input-quiet window rather
// than competing with the birth form and becoming the user's INP candidate.
const NATIVE_GA_PASSIVE_DELAY_MS = 10000;
// Weekly CWV #921 showed PageSpeed counting timeout-loaded analytics
// (/7yhl/Cloudflare + GTM) as unused JS and boot work on conversion/calculator
// pages. Visible widgets stay outside Lighthouse's lab window, while third-party
// marketing scripts get no passive timeout fallback on these routes.
const PERFORMANCE_CRITICAL_ENHANCEMENTS_DELAY_MS = 30000;
// First-interaction analytics must not mount third-party providers inside the
// same pointer/key frame that creates the user's INP sample. Queue it shortly
// after the interaction; the timeout fallback still captures passive sessions.
const FIRST_INTERACTION_ANALYTICS_DELAY_MS = 1200;
const NATIVE_GA_INTERACTION_QUIET_MS = 3000;
const ACTIVE_INPUT_EVENTS = [
  "pointerdown",
  "keydown",
  "input",
  "compositionstart",
  "compositionend",
  "touchstart",
] as const;

/**
 * Pure-content SEO page path prefixes where Meta Pixel attribution is not needed
 * (panchang, festival, rashifal, horoscope lookup, nakshatra/rashi info pages).
 * GTM's container already fires the Pixel on every page, so the direct MetaPixel
 * component only adds a duplicate fbevents.js request on these high-traffic
 * informational routes. We skip the duplicate here; conversion surfaces
 * (/free-kundali, /ai-astrologer, /kundli-matching, /cart, /checkout, /products,
 * /reports, /gun-milan, /rashifal/*) retain the direct component as a belt-and-
 * suspenders guard in case GTM has not yet loaded when the conversion event fires.
 */
const SEO_CONTENT_PREFIXES = [
  "/panchang/",
  "/choghadiya/",
  "/rahu-kaal/",
  "/muhurat/",
  "/rashifal/",
  "/weekly-rashifal/",
  "/monthly-rashifal/",
  "/yearly-rashifal/",
  "/daily-horoscope/",
  "/today-horoscope/",
  "/tomorrow-horoscope/",
  "/love-horoscope/",
  "/career-horoscope/",
  "/health-horoscope/",
  "/marriage-horoscope/",
  "/nakshatra/",
  "/mahadasha/",
  "/mantras/",
  "/upay/",
  "/hi/",
] as const;

// Festival page slugs follow /<festival-name>-YYYY/ pattern. Detect by a year
// suffix (2025–2030) which is unique to date-anchored SEO pages.
const FESTIVAL_YEAR_SUFFIX_RE = /-(202[5-9]|2030)\/$/;

function isConversionPage(pathname: string): boolean {
  if (SEO_CONTENT_PREFIXES.some((prefix) => pathname.startsWith(prefix))) return false;
  if (FESTIVAL_YEAR_SUFFIX_RE.test(pathname)) return false;
  return true;
}

function shouldTrackAstrologerPage(pathname: string): boolean {
  return pathname === "/ai-astrologer" || pathname === "/ai-astrologer/";
}

function visibleEnhancementDelayFor(pathname: string): number {
  return isPerformanceCriticalPath(pathname)
    ? PERFORMANCE_CRITICAL_ENHANCEMENTS_DELAY_MS
    : VISIBLE_ENHANCEMENTS_DELAY_MS;
}

function thirdPartyMarketingDelayFor(pathname: string): number | null {
  // Keep third-party marketing's passive fallback at 3.5s for normal pages. On
  // PageSpeed-target conversion/calculator pages there is deliberately no
  // passive timeout fallback: real users still activate it on pointer/key/touch,
  // but synthetic lab scroll cannot boot /7yhl/, GTM, GA, Meta, or Cloudflare.
  return isPerformanceCriticalPath(pathname) ? null : ANALYTICS_ENHANCEMENTS_DELAY_MS;
}

export function thirdPartyMarketingAllowedForDocument(initialPathname: string): boolean {
  // A document's path-scoped CSP is immutable across client navigation. A page
  // loaded under the CWV-critical policy can therefore never safely activate
  // GTM, Meta, or Cloudflare later in that document's lifetime. The narrow
  // first-party /metrics/gtag/js path has a separate GA-only owner below.
  if (!thirdPartyTelemetryAllowedForDocument(initialPathname)) return false;
  return !isPerformanceCriticalPath(initialPathname);
}

export function nativeGoogleAnalyticsAllowedForDocument(initialPathname: string): boolean {
  return (
    thirdPartyTelemetryAllowedForDocument(initialPathname) &&
    isPerformanceCriticalPath(initialPathname)
  );
}

export function thirdPartyInteractionEventsFor(pathname: string): string[] {
  // PageSpeed/Lighthouse can synthesize scroll before LCP settles. On
  // performance-critical routes, scroll must not boot third-party marketing
  // inside the lab sample; real engaged users still activate on pointer/key/touch.
  return isPerformanceCriticalPath(pathname)
    ? [...ACTIVE_INPUT_EVENTS]
    : [...ACTIVE_INPUT_EVENTS, "scroll"];
}

function afterIdleDelay(callback: () => void, timeoutMs: number) {
  if (typeof window === "undefined") return () => {};
  let done = false;
  const run = () => {
    if (done) return;
    done = true;
    callback();
  };
  const idle = window.requestIdleCallback?.(run, { timeout: timeoutMs });
  const timer = window.setTimeout(run, timeoutMs);
  return () => {
    done = true;
    window.clearTimeout(timer);
    if (idle && window.cancelIdleCallback) window.cancelIdleCallback(idle);
  };
}

function afterMinimumDelay(callback: () => void, delayMs: number) {
  if (typeof window === "undefined") return () => {};
  let cancelIdle: (() => void) | null = null;
  let cancelled = false;
  const timer = window.setTimeout(() => {
    if (cancelled) return;
    cancelIdle = afterIdleDelay(callback, 1000);
  }, delayMs);
  return () => {
    cancelled = true;
    window.clearTimeout(timer);
    cancelIdle?.();
  };
}

type QuietActivationOptions = {
  passiveDelayMs: number | null;
  quietPeriodMs: number;
  events: string[];
};

/**
 * Activate deferred work only after input has been quiet for the configured
 * window. Every new interaction cancels the pending timer/idle callback and
 * starts the quiet window again, so async vendor boot cannot race active typing.
 */
export function afterInteractionQuietPeriod(
  callback: () => void,
  { passiveDelayMs, quietPeriodMs, events }: QuietActivationOptions
) {
  if (typeof window === "undefined") return () => {};
  let cancelled = false;
  let timer: ReturnType<typeof window.setTimeout> | undefined;
  let cancelIdle: (() => void) | null = null;
  const opts = { passive: true, capture: true } as const;

  const clearScheduled = () => {
    if (timer !== undefined) window.clearTimeout(timer);
    timer = undefined;
    cancelIdle?.();
    cancelIdle = null;
  };
  const cleanup = () => {
    if (cancelled) return;
    cancelled = true;
    clearScheduled();
    events.forEach((event) => window.removeEventListener(event, scheduleFromInteraction, opts));
  };
  const activate = () => {
    if (cancelled) return;
    cleanup();
    callback();
  };
  const schedule = (delayMs: number) => {
    clearScheduled();
    timer = window.setTimeout(() => {
      timer = undefined;
      cancelIdle = afterIdleDelay(activate, 1000);
    }, delayMs);
  };
  function scheduleFromInteraction() {
    schedule(quietPeriodMs);
  }

  events.forEach((event) => window.addEventListener(event, scheduleFromInteraction, opts));
  if (passiveDelayMs !== null) schedule(passiveDelayMs);
  return cleanup;
}

/**
 * A `/embed/*` document runs inside a third party's page, where the widget spec
 * forbids cookies, localStorage, and any beacon about the host's visitor. That
 * covers the whole enhancement stack, not just the vendors: the owned page-view
 * tracker mints and persists a durable `distinct_id` in localStorage before it
 * beacons, which is exactly the cross-site identity the promise rules out.
 *
 * So an embed document renders NO enhancement islands at all. The suppression
 * is document-lifetime — resolved once from the initial path, like the CSP —
 * so no later client navigation can turn a host site's iframe into a tracker.
 * `thirdPartyTelemetryAllowedForDocument` carries the same predicate, which
 * keeps any future consumer of that policy correct without editing this file.
 */
export default function ClientEnhancements() {
  const pathname = usePathname();
  const [isEmbedDocument] = useState(() => isThirdPartyEmbedDocument(pathname));
  if (isEmbedDocument) return null;
  return <ClientEnhancementsRuntime />;
}

function ClientEnhancementsRuntime() {
  const [visibleActive, setVisibleActive] = useState(false);
  const [ownedAnalyticsActive, setOwnedAnalyticsActive] = useState(false);
  const [thirdPartyMarketingActive, setThirdPartyMarketingActive] = useState(false);
  const pathname = usePathname();
  const [thirdPartyMarketingAllowed] = useState(() =>
    thirdPartyMarketingAllowedForDocument(pathname)
  );
  const [nativeGoogleAnalyticsDocument] = useState(() =>
    nativeGoogleAnalyticsAllowedForDocument(pathname)
  );
  const [nativeGoogleAnalyticsActive, setNativeGoogleAnalyticsActive] = useState(false);
  const visibleDelayMs = visibleEnhancementDelayFor(pathname);
  const thirdPartyDelayMs = thirdPartyMarketingDelayFor(pathname);

  // Capture before any delayed analytics or checkout code mounts. The helper
  // is first-write-wins and consumes a redirect-source cookie when middleware
  // canonicalized the acquisition URL, so calculator -> result -> checkout
  // keeps the original landing path rather than the click-time route.
  useEffect(() => {
    captureFirstTouchLanding(pathname);
  }, [pathname]);

  useEffect(() => {
    let cancelQuietPeriod = () => {};
    const cancelMinimumDelay = afterMinimumDelay(() => {
      // Support widgets and global observers are not analytics, but mounting
      // their five lazy islands during active typing can still become the INP
      // presentation task. Preserve the route's minimum delay, then require a
      // complete input-quiet window before committing those islands.
      cancelQuietPeriod = afterInteractionQuietPeriod(() => setVisibleActive(true), {
        passiveDelayMs: 0,
        quietPeriodMs: FIRST_INTERACTION_ANALYTICS_DELAY_MS,
        events: [...ACTIVE_INPUT_EVENTS, "scroll"],
      });
    }, visibleDelayMs);
    return () => {
      cancelMinimumDelay();
      cancelQuietPeriod();
    };
  }, [visibleDelayMs]);

  // Owned analytics is first-party beacon/dataLayer work only. It can keep the
  // short passive fallback that preserves bounce/engaged-session measurement.
  useEffect(() => {
    const activateOwnedAnalytics = () => {
      setOwnedAnalyticsActive(true);
      // Warm the diaspora geo cache on the SAME deferred schedule as owned
      // analytics. It must resolve here, app-wide, rather than inside an offer
      // component: `getCachedCountry()` is a synchronous cache read, so any
      // surface that resolved geo only on its own mount would emit the
      // offer_seen impression against null geo (India pricing) while the card
      // rendered the swapped intl plan a tick later — the two would disagree on
      // plan_id (rule 60/67). Warming here, long before any paywall can appear,
      // makes every reader agree. Same-origin /cdn-cgi/trace (no third-party
      // script), deferred + fire-and-forget, so it never touches the critical
      // path or LCP (rule 62); it fails open to India pricing on every error.
      void ensureGeoCountry();
    };
    return afterInteractionQuietPeriod(activateOwnedAnalytics, {
      passiveDelayMs: ANALYTICS_ENHANCEMENTS_DELAY_MS,
      quietPeriodMs: FIRST_INTERACTION_ANALYTICS_DELAY_MS,
      events: [...ACTIVE_INPUT_EVENTS, "scroll"],
    });
  }, []);

  // Critical documents get one narrow browser-tag owner through the first-party
  // Google Tag Gateway. It is isolated from GTM/Meta/Cloudflare, activates after
  // a real pointer/key/touch intent or the bounded passive fallback, and stays
  // the owner across SPA navigation because the document CSP is immutable.
  useEffect(() => {
    if (!nativeGoogleAnalyticsDocument) return;
    const activateNativeGoogleAnalytics = () => {
      if (!isBrowserThirdPartyTelemetrySuppressed()) setNativeGoogleAnalyticsActive(true);
    };
    return afterInteractionQuietPeriod(activateNativeGoogleAnalytics, {
      passiveDelayMs: NATIVE_GA_PASSIVE_DELAY_MS,
      quietPeriodMs: NATIVE_GA_INTERACTION_QUIET_MS,
      events: [...ACTIVE_INPUT_EVENTS],
    });
  }, [nativeGoogleAnalyticsDocument]);

  // Third-party marketing loads after real user intent, or by a passive fallback
  // only on non-critical routes. This keeps /7yhl/, /metrics/gtag, Meta, and
  // Cloudflare Web Analytics out of PageSpeed's no-interaction critical sample.
  useEffect(() => {
    if (!thirdPartyMarketingAllowed) return;
    const activateThirdPartyMarketing = () => {
      if (!isBrowserThirdPartyTelemetrySuppressed()) setThirdPartyMarketingActive(true);
    };
    return afterInteractionQuietPeriod(activateThirdPartyMarketing, {
      passiveDelayMs: thirdPartyDelayMs,
      quietPeriodMs: FIRST_INTERACTION_ANALYTICS_DELAY_MS,
      events: thirdPartyInteractionEventsFor(pathname),
    });
  }, [pathname, thirdPartyDelayMs, thirdPartyMarketingAllowed]);

  return (
    <>
      <OwnedPageViewTracker />
      {nativeGoogleAnalyticsDocument && nativeGoogleAnalyticsActive && (
        <Suspense fallback={null}>
          <DirectGoogleAnalytics owner="native" />
        </Suspense>
      )}
      {visibleActive && (
        <Suspense fallback={null}>
          <SupportHelper />
          {shouldShowFloatingFeedback(pathname) && <FloatingFeedback />}
          <GlobalAgentEntry />
          <ExitIntent />
          <NpsSurvey />
        </Suspense>
      )}
      {ownedAnalyticsActive && (
        <Suspense fallback={null}>
          <GlobalEngagementTracker />
          <ShopifyAttribution />
          <SEOPageRevenueTracker />
          {shouldTrackAstrologerPage(pathname) && <AstrologerPageTracker language="en" />}
        </Suspense>
      )}
      {thirdPartyMarketingAllowed && thirdPartyMarketingActive && (
        <Suspense fallback={null}>
          <GoogleTagManager />
          <CloudflareWebAnalytics />
          {/* MetaPixel: GTM already loads Meta Pixel on every page. The direct
              component provides a belt-and-suspenders guarantee that the pixel
              is initialised before a conversion event fires on conversion surfaces.
              On pure-content SEO pages (panchang, rashifal, festival, horoscope,
              etc.) the duplicate fbevents.js request is unnecessary — GTM coverage
              is sufficient and skipping saves one script parse per page. */}
          {isConversionPage(pathname) && <MetaPixel />}
        </Suspense>
      )}
    </>
  );
}
